import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RNG.css';

const RNG = () => {
    // Dice Roller State
    const [numDice, setNumDice] = useState(2);
    const [diceType, setDiceType] = useState(6);
    const [diceResults, setDiceResults] = useState([]);
    const [diceTotal, setDiceTotal] = useState(0);
    const [isRolling, setIsRolling] = useState(false);
    const [diceInterval, setDiceInterval] = useState(5);
    const [diceIsActive, setDiceIsActive] = useState(false);
    const [diceCountdown, setDiceCountdown] = useState(0);
    const diceTimerRef = useRef(null);

    // Custom Range RNG State
    const [customMin, setCustomMin] = useState(0);
    const [customMax, setCustomMax] = useState(100);
    const [customResult, setCustomResult] = useState(null);
    const [customInterval, setCustomInterval] = useState(5);
    const [customIsActive, setCustomIsActive] = useState(false);
    const [customCountdown, setCustomCountdown] = useState(0);
    const customTimerRef = useRef(null);

    // Auto-Reset RNG State
    const [autoMin, setAutoMin] = useState(0);
    const [autoMax, setAutoMax] = useState(100);
    const [autoInterval, setAutoInterval] = useState(5);
    const [autoResult, setAutoResult] = useState(null);
    const [autoIsActive, setAutoIsActive] = useState(false);
    const [autoCountdown, setAutoCountdown] = useState(0);
    const autoTimerRef = useRef(null);

    // Coin Flip State
    const [coinResult, setCoinResult] = useState(null);
    const [isFlipping, setIsFlipping] = useState(false);
    const [coinInterval, setCoinInterval] = useState(5);
    const [coinIsActive, setCoinIsActive] = useState(false);
    const [coinCountdown, setCoinCountdown] = useState(0);
    const coinTimerRef = useRef(null);

    // Action Randomizer State
    const [actions, setActions] = useState([
        { text: 'Fold', weight: 1 },
        { text: 'Call', weight: 1 },
        { text: 'Raise 2.5x', weight: 1 },
        { text: 'Raise 3x', weight: 1 }
    ]);
    const [selectedAction, setSelectedAction] = useState(null);
    const [actionInterval, setActionInterval] = useState(5);
    const [actionIsActive, setActionIsActive] = useState(false);
    const [actionCountdown, setActionCountdown] = useState(0);
    const actionTimerRef = useRef(null);

    // GTO Frequency Generator State
    const [gtoFrequency, setGtoFrequency] = useState(33);
    const [gtoAction, setGtoAction] = useState('');
    const [gtoNoAction, setGtoNoAction] = useState('');
    const [gtoResult, setGtoResult] = useState(null);
    const [gtoInterval, setGtoInterval] = useState(5);
    const [gtoIsActive, setGtoIsActive] = useState(false);
    const [gtoCountdown, setGtoCountdown] = useState(0);
    const gtoTimerRef = useRef(null);

    // Percentage Wheel State
    const [percentResult, setPercentResult] = useState(null);
    const [percentInterval, setPercentInterval] = useState(5);
    const [percentIsActive, setPercentIsActive] = useState(false);
    const [percentCountdown, setPercentCountdown] = useState(0);
    const percentTimerRef = useRef(null);

    // Dice Roller Functions
    const rollDice = useCallback(() => {
        setIsRolling(true);
        // Animate rolling for 500ms
        setTimeout(() => {
            const results = [];
            let total = 0;
            for (let i = 0; i < numDice; i++) {
                const roll = Math.floor(Math.random() * diceType) + 1;
                results.push(roll);
                total += roll;
            }
            setDiceResults(results);
            setDiceTotal(total);
            setIsRolling(false);
            if (diceIsActive) {
                setDiceCountdown(diceInterval);
            }
        }, 500);
    }, [numDice, diceType, diceIsActive, diceInterval]);

    // Custom Range RNG Functions
    const generateCustomNumber = useCallback(() => {
        const min = Math.min(customMin, customMax);
        const max = Math.max(customMin, customMax);
        const result = Math.floor(Math.random() * (max - min + 1)) + min;
        setCustomResult(result);
        if (customIsActive) {
            setCustomCountdown(customInterval);
        }
    }, [customMin, customMax, customIsActive, customInterval]);

    // Auto-Reset RNG Functions
    const generateAutoNumber = useCallback(() => {
        const min = Math.min(autoMin, autoMax);
        const max = Math.max(autoMin, autoMax);
        const result = Math.floor(Math.random() * (max - min + 1)) + min;
        setAutoResult(result);
        setAutoCountdown(autoInterval);
    }, [autoMin, autoMax, autoInterval]);

    // Coin Flip Functions
    const flipCoin = useCallback(() => {
        setIsFlipping(true);
        setTimeout(() => {
            const result = Math.random() < 0.5 ? 'HEADS' : 'TAILS';
            setCoinResult(result);
            setIsFlipping(false);
            if (coinIsActive) {
                setCoinCountdown(coinInterval);
            }
        }, 600);
    }, [coinIsActive, coinInterval]);

    // Action Randomizer Functions
    const addAction = () => {
        setActions([...actions, { text: '', weight: 1 }]);
    };

    const updateAction = (index, field, value) => {
        const newActions = [...actions];
        newActions[index][field] = field === 'weight' ? parseFloat(value) || 1 : value;
        setActions(newActions);
    };

    const removeAction = (index) => {
        if (actions.length > 1) {
            setActions(actions.filter((_, i) => i !== index));
        }
    };

    const selectRandomAction = useCallback(() => {
        const totalWeight = actions.reduce((sum, action) => sum + action.weight, 0);
        let random = Math.random() * totalWeight;
        
        for (const action of actions) {
            random -= action.weight;
            if (random <= 0) {
                setSelectedAction(action.text);
                if (actionIsActive) {
                    setActionCountdown(actionInterval);
                }
                return;
            }
        }
    }, [actions, actionIsActive, actionInterval]);

    // GTO Frequency Generator Functions
    const generateGTODecision = useCallback(() => {
        const random = Math.random() * 100;
        const result = random <= gtoFrequency;
        setGtoResult(result);
        if (gtoIsActive) {
            setGtoCountdown(gtoInterval);
        }
    }, [gtoFrequency, gtoIsActive, gtoInterval]);

    // Percentage Wheel Functions
    const spinPercentageWheel = useCallback(() => {
        const result = Math.floor(Math.random() * 101);
        setPercentResult(result);
        if (percentIsActive) {
            setPercentCountdown(percentInterval);
        }
    }, [percentIsActive, percentInterval]);

    // Toggle Timer Functions
    const toggleDiceTimer = () => {
        if (diceIsActive) {
            setDiceIsActive(false);
            if (diceTimerRef.current) {
                clearInterval(diceTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!diceInterval || diceInterval < 1) {
                setDiceInterval(5);
            }
            setDiceIsActive(true);
            rollDice();
        }
    };

    const toggleCustomTimer = () => {
        if (customIsActive) {
            setCustomIsActive(false);
            if (customTimerRef.current) {
                clearInterval(customTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!customInterval || customInterval < 1) {
                setCustomInterval(5);
            }
            setCustomIsActive(true);
            generateCustomNumber();
        }
    };

    const toggleAutoRNG = () => {
        if (autoIsActive) {
            setAutoIsActive(false);
            if (autoTimerRef.current) {
                clearInterval(autoTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!autoInterval || autoInterval < 1) {
                setAutoInterval(5);
            }
            setAutoIsActive(true);
            generateAutoNumber();
        }
    };

    const toggleCoinTimer = () => {
        if (coinIsActive) {
            setCoinIsActive(false);
            if (coinTimerRef.current) {
                clearInterval(coinTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!coinInterval || coinInterval < 1) {
                setCoinInterval(5);
            }
            setCoinIsActive(true);
            flipCoin();
        }
    };

    const toggleActionTimer = () => {
        if (actionIsActive) {
            setActionIsActive(false);
            if (actionTimerRef.current) {
                clearInterval(actionTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!actionInterval || actionInterval < 1) {
                setActionInterval(5);
            }
            setActionIsActive(true);
            selectRandomAction();
        }
    };

    const toggleGtoTimer = () => {
        if (gtoIsActive) {
            setGtoIsActive(false);
            if (gtoTimerRef.current) {
                clearInterval(gtoTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!gtoInterval || gtoInterval < 1) {
                setGtoInterval(5);
            }
            setGtoIsActive(true);
            generateGTODecision();
        }
    };

    const togglePercentTimer = () => {
        if (percentIsActive) {
            setPercentIsActive(false);
            if (percentTimerRef.current) {
                clearInterval(percentTimerRef.current);
            }
        } else {
            // Default to 5 if invalid
            if (!percentInterval || percentInterval < 1) {
                setPercentInterval(5);
            }
            setPercentIsActive(true);
            spinPercentageWheel();
        }
    };

    // Timer Effects
    useEffect(() => {
        if (autoIsActive) {
            autoTimerRef.current = setInterval(() => {
                setAutoCountdown(prev => {
                    if (prev <= 1) {
                        generateAutoNumber();
                        return autoInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (autoTimerRef.current) {
                clearInterval(autoTimerRef.current);
            }
        };
    }, [autoIsActive, autoInterval, generateAutoNumber]);

    useEffect(() => {
        if (customIsActive) {
            customTimerRef.current = setInterval(() => {
                setCustomCountdown(prev => {
                    if (prev <= 1) {
                        generateCustomNumber();
                        return customInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (customTimerRef.current) {
                clearInterval(customTimerRef.current);
            }
        };
    }, [customIsActive, customInterval, generateCustomNumber]);

    useEffect(() => {
        if (percentIsActive) {
            percentTimerRef.current = setInterval(() => {
                setPercentCountdown(prev => {
                    if (prev <= 1) {
                        spinPercentageWheel();
                        return percentInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (percentTimerRef.current) {
                clearInterval(percentTimerRef.current);
            }
        };
    }, [percentIsActive, percentInterval, spinPercentageWheel]);

    useEffect(() => {
        if (diceIsActive) {
            diceTimerRef.current = setInterval(() => {
                setDiceCountdown(prev => {
                    if (prev <= 1) {
                        rollDice();
                        return diceInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (diceTimerRef.current) {
                clearInterval(diceTimerRef.current);
            }
        };
    }, [diceIsActive, diceInterval, rollDice]);

    useEffect(() => {
        if (coinIsActive) {
            coinTimerRef.current = setInterval(() => {
                setCoinCountdown(prev => {
                    if (prev <= 1) {
                        flipCoin();
                        return coinInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (coinTimerRef.current) {
                clearInterval(coinTimerRef.current);
            }
        };
    }, [coinIsActive, coinInterval, flipCoin]);

    useEffect(() => {
        if (actionIsActive) {
            actionTimerRef.current = setInterval(() => {
                setActionCountdown(prev => {
                    if (prev <= 1) {
                        selectRandomAction();
                        return actionInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (actionTimerRef.current) {
                clearInterval(actionTimerRef.current);
            }
        };
    }, [actionIsActive, actionInterval, selectRandomAction]);

    useEffect(() => {
        if (gtoIsActive) {
            gtoTimerRef.current = setInterval(() => {
                setGtoCountdown(prev => {
                    if (prev <= 1) {
                        generateGTODecision();
                        return gtoInterval;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (gtoTimerRef.current) {
                clearInterval(gtoTimerRef.current);
            }
        };
    }, [gtoIsActive, gtoInterval, generateGTODecision]);

    return (
        <div className="poker-container">
            <div className="rng-container">
                <h1>Random Number Generators</h1>
                <p>Welcome to the RNG Tools at Gambler.Tools! Use these tools to make random decisions while playing poker.</p>

                {/* Dice Roller */}
                <div className="rng-section">
                    <h2>Dice Roller</h2>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Number of Dice:</label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={numDice}
                                onChange={(e) => setNumDice(parseInt(e.target.value) || 1)}
                                disabled={diceIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Dice Type:</label>
                            <select 
                                value={diceType} 
                                onChange={(e) => setDiceType(parseInt(e.target.value))}
                                disabled={diceIsActive}
                            >
                                <option value={4}>d4</option>
                                <option value={6}>d6</option>
                                <option value={8}>d8</option>
                                <option value={10}>d10</option>
                                <option value={12}>d12</option>
                                <option value={20}>d20</option>
                                <option value={100}>d100</option>
                            </select>
                        </div>
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={diceInterval}
                                onChange={(e) => setDiceInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={diceIsActive}
                            />
                        </div>
                    </div>
                    <div className="timer-buttons-group">
                        <button className="rng-button" onClick={rollDice} disabled={isRolling}>
                            {isRolling ? 'Rolling...' : 'Roll Dice'}
                        </button>
                        <button 
                            className={`rng-button ${diceIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={toggleDiceTimer}
                        >
                            {diceIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {diceResults.length > 0 && (
                        <div className={`rng-result ${diceIsActive && diceCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className="dice-results">
                                {diceResults.map((result, index) => (
                                    <div key={index} className={`die ${isRolling ? 'rolling' : ''}`}>
                                        {result}
                                    </div>
                                ))}
                            </div>
                            <div className="result-total">Total: {diceTotal}</div>
                            {diceIsActive && (
                                <div className="countdown">Next in {diceCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Custom Range RNG */}
                <div className="rng-section">
                    <h2>Custom Range RNG</h2>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Min:</label>
                            <input
                                type="number"
                                value={customMin}
                                onChange={(e) => setCustomMin(parseInt(e.target.value) || 0)}
                                disabled={customIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Max:</label>
                            <input
                                type="number"
                                value={customMax}
                                onChange={(e) => setCustomMax(parseInt(e.target.value) || 0)}
                                disabled={customIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={customInterval}
                                onChange={(e) => setCustomInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={customIsActive}
                            />
                        </div>
                    </div>
                    <div className="timer-buttons-group">
                        <button className="rng-button" onClick={generateCustomNumber}>
                            Generate Number
                        </button>
                        <button 
                            className={`rng-button ${customIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={toggleCustomTimer}
                        >
                            {customIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {customResult !== null && (
                        <div className={`rng-result ${customIsActive && customCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className="result-value">{customResult}</div>
                            {customIsActive && (
                                <div className="countdown">Next in {customCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Auto-Reset RNG */}
                <div className="rng-section">
                    <h2>Auto-Reset RNG</h2>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Min:</label>
                            <input
                                type="number"
                                value={autoMin}
                                onChange={(e) => setAutoMin(parseInt(e.target.value) || 0)}
                                disabled={autoIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Max:</label>
                            <input
                                type="number"
                                value={autoMax}
                                onChange={(e) => setAutoMax(parseInt(e.target.value) || 0)}
                                disabled={autoIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={autoInterval}
                                onChange={(e) => setAutoInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={autoIsActive}
                            />
                        </div>
                    </div>
                    <button 
                        className={`rng-button ${autoIsActive ? 'stop-button' : ''}`} 
                        onClick={toggleAutoRNG}
                    >
                        {autoIsActive ? 'Stop' : 'Start Auto-RNG'}
                    </button>
                    {autoResult !== null && (
                        <div className={`rng-result ${autoIsActive && autoCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className="result-value">{autoResult}</div>
                            {autoIsActive && (
                                <div className="countdown">Next in {autoCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Coin Flip */}
                <div className="rng-section">
                    <h2>Coin Flip</h2>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={coinInterval}
                                onChange={(e) => setCoinInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={coinIsActive}
                            />
                        </div>
                    </div>
                    <div className="timer-buttons-group">
                        <button className="rng-button" onClick={flipCoin} disabled={isFlipping}>
                            {isFlipping ? 'Flipping...' : 'Flip Coin'}
                        </button>
                        <button 
                            className={`rng-button ${coinIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={toggleCoinTimer}
                        >
                            {coinIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {coinResult && (
                        <div className={`rng-result ${coinIsActive && coinCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className={`coin-result ${coinResult.toLowerCase()} ${isFlipping ? 'flipping' : ''}`}>
                                {isFlipping ? (
                                    <span className="coin-flip-animation">
                                        <span>HEADS</span>
                                        <span>TAILS</span>
                                    </span>
                                ) : (
                                    coinResult
                                )}
                            </div>
                            {coinIsActive && (
                                <div className="countdown">Next in {coinCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Action Randomizer */}
                <div className="rng-section">
                    <h2>Action Randomizer</h2>
                    <div className="action-list">
                        {actions.map((action, index) => (
                            <div key={index} className="action-item">
                                <input
                                    type="text"
                                    placeholder="Action name"
                                    value={action.text}
                                    onChange={(e) => updateAction(index, 'text', e.target.value)}
                                    className="action-input"
                                    disabled={actionIsActive}
                                />
                                <input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    value={action.weight}
                                    onChange={(e) => updateAction(index, 'weight', e.target.value)}
                                    className="weight-input"
                                    title="Weight"
                                    disabled={actionIsActive}
                                />
                                <button 
                                    className="remove-action-button" 
                                    onClick={() => removeAction(index)}
                                    disabled={actions.length <= 1 || actionIsActive}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={actionInterval}
                                onChange={(e) => setActionInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={actionIsActive}
                            />
                        </div>
                    </div>
                    <div className="action-buttons-group">
                        <button className="rng-button secondary" onClick={addAction} disabled={actionIsActive}>
                            Add Action
                        </button>
                        <button className="rng-button" onClick={selectRandomAction}>
                            Select Random Action
                        </button>
                        <button 
                            className={`rng-button ${actionIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={toggleActionTimer}
                        >
                            {actionIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {selectedAction && (
                        <div className={`rng-result ${actionIsActive && actionCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className="result-value action-result">{selectedAction}</div>
                            {actionIsActive && (
                                <div className="countdown">Next in {actionCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* GTO Frequency Generator */}
                <div className="rng-section">
                    <h2>GTO Frequency Generator</h2>
                    <div className="rng-controls">
                        <div className="control-group full-width">
                            <label>Frequency: {gtoFrequency}%</label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={gtoFrequency}
                                onChange={(e) => setGtoFrequency(parseInt(e.target.value))}
                                className="frequency-slider"
                                disabled={gtoIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Action (if Yes):</label>
                            <input
                                type="text"
                                placeholder="e.g., Bluff"
                                value={gtoAction}
                                onChange={(e) => setGtoAction(e.target.value)}
                                disabled={gtoIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Action (if No):</label>
                            <input
                                type="text"
                                placeholder="e.g., Don't Bluff"
                                value={gtoNoAction}
                                onChange={(e) => setGtoNoAction(e.target.value)}
                                disabled={gtoIsActive}
                            />
                        </div>
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={gtoInterval}
                                onChange={(e) => setGtoInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={gtoIsActive}
                            />
                        </div>
                    </div>
                    <div className="timer-buttons-group">
                        <button className="rng-button" onClick={generateGTODecision}>
                            Generate Decision
                        </button>
                        <button 
                            className={`rng-button ${gtoIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={toggleGtoTimer}
                        >
                            {gtoIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {gtoResult !== null && (
                        <div className={`rng-result ${gtoIsActive && gtoCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className={`result-value gto-result ${gtoResult ? 'yes' : 'no'}`}>
                                {gtoResult 
                                    ? (gtoAction || 'YES') 
                                    : (gtoNoAction || 'NO')
                                }
                            </div>
                            {gtoIsActive && (
                                <div className="countdown">Next in {gtoCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Percentage Wheel */}
                <div className="rng-section">
                    <h2>Percentage Wheel</h2>
                    <div className="rng-controls">
                        <div className="control-group">
                            <label>Timer Interval (seconds):</label>
                            <input
                                type="number"
                                min="1"
                                max="60"
                                value={percentInterval}
                                onChange={(e) => setPercentInterval(e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={percentIsActive}
                            />
                        </div>
                    </div>
                    <div className="timer-buttons-group">
                        <button className="rng-button" onClick={spinPercentageWheel}>
                            Spin Wheel
                        </button>
                        <button 
                            className={`rng-button ${percentIsActive ? 'stop-button' : 'secondary'}`}
                            onClick={togglePercentTimer}
                        >
                            {percentIsActive ? 'Stop Timer' : 'Start Timer'}
                        </button>
                    </div>
                    {percentResult !== null && (
                        <div className={`rng-result ${percentIsActive && percentCountdown <= 1 ? 'fading-out' : ''}`}>
                            <div className="result-value percent-result">{percentResult}%</div>
                            {percentIsActive && (
                                <div className="countdown">Next in {percentCountdown}s</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RNG;

